import { useQuery } from '@tanstack/react-query'
import { Database, HardDrive, KeyRound, LockKeyhole, Mail, Mic, Server, ShieldCheck } from 'lucide-react'
import { api } from '@/lib/api'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import type { DashboardMetrics } from '@/types'
import { LegacyPage } from '@/components/layout/LegacyPage'
import { TrustSafetyHost } from '@/components/legacy/TrustSafetyHost'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { QueryError } from '@/components/ui/QueryError'

const SERVICE_META: Record<string, { label: string; icon: typeof Server }> = {
  api: { label: 'Cloudflare API', icon: Server },
  database: { label: 'D1 Database', icon: Database },
  voice: { label: 'P2P call signaling', icon: Mic },
  storage: { label: 'R2 Storage', icon: HardDrive },
  email: { label: 'Transactional email', icon: Mail },
}

function statusVariant(s: string) {
  if (s === 'operational') return 'success' as const
  if (s === 'degraded' || s === 'unknown' || s === 'n/a') return 'warning' as const
  return 'danger' as const
}

function PlatformTabs({ active }: { active: 'services' | 'voice' }) {
  return (
    <nav className="admin-filter-tabs" aria-label="Platform health sections">
      <Link className={cn('admin-filter-tab inline-flex items-center', active === 'services' && 'is-active')} to="/infrastructure">Services &amp; security</Link>
      <Link className={cn('admin-filter-tab inline-flex items-center', active === 'voice' && 'is-active')} to="/voice">Voice &amp; calls</Link>
    </nav>
  )
}

export function InfrastructurePage({ variant }: { variant?: 'voice' }) {
  const { data, dataUpdatedAt, error, isPending, refetch } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api<DashboardMetrics>('/api/admin/dashboard'),
    refetchInterval: 20_000,
  })
  if (error) return <QueryError title="Couldn’t load platform health" error={error} onRetry={() => void refetch()} />
  if (isPending) return <div role="status"><PageHeader title="Platform health" description="Checking service health…" /></div>

  if (variant === 'voice') {
    return (
      <div className="space-y-8">
        <PageHeader
          eyebrow="Platform"
          title="Voice administration"
          description="WebRTC mesh signaling health. Clyr coordinates peers but cannot access call media."
        />
        <PlatformTabs active="voice" />
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: 'Signaling', value: data?.voice.status || 'checking…', sub: `Durable Objects · protocol v${data?.voice.protocolVersion || 2}` },
            { label: 'Media path', value: 'Encrypted WebRTC', sub: 'Direct when possible · TURN fallback' },
            { label: 'NAT fallback', value: data?.voice.relayAvailable ? 'TURN ready' : 'STUN only', sub: data?.voice.relayAvailable ? 'Short-lived relay credentials' : 'Configure TURN secrets for restrictive networks' },
          ].map((item) => (
            <Card key={item.label}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="h-4 w-4 text-[#40dbf1]" />
                  {item.label}
                </CardTitle>
                <CardDescription>{item.sub}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold capitalize tracking-[-0.02em]">{item.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <LegacyPage title="Status page" description="Manage incidents, maintenance, and service health on the public status page.">
          <TrustSafetyHost tab="status" />
        </LegacyPage>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Platform"
        title="Infrastructure"
        description={
          data
            ? `Live service health · Updated ${new Date(dataUpdatedAt || 0).toLocaleTimeString()}`
            : 'Platform infrastructure monitoring'
        }
      />
      <PlatformTabs active="services" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data &&
          Object.entries(data.infrastructure).filter(([,status])=>status!=='n/a').map(([key, status]) => {
            const meta = SERVICE_META[key] || { label: key, icon: Server }
            const Icon = meta.icon
            return (
              <Card key={key}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(64,219,241,0.1)] text-[#40dbf1] ring-1 ring-[rgba(64,219,241,0.18)]">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#f0f4f8]">{meta.label}</p>
                        <p className="text-xs text-[#93a5ad] mt-0.5 capitalize">{key}</p>
                      </div>
                    </div>
                    <Badge variant={statusVariant(status)}>{status}</Badge>
                  </div>
                </CardContent>
              </Card>
            )
          })}
      </div>

      {data?.encryption && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LockKeyhole className="h-4 w-4 text-[#40dbf1]" />
              End-to-end encryption boundary
            </CardTitle>
            <CardDescription>
              Admin can inspect delivery metadata and user-disclosed report snippets, never ordinary message bodies.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Encrypted messages', value: data.encryption.encryptedMessages, icon: ShieldCheck },
              { label: 'Legacy migration queue', value: data.encryption.legacyMessages, icon: KeyRound },
              { label: 'Active secure devices', value: data.encryption.activeDevices, icon: LockKeyhole },
              { label: 'User disclosures', value: data.encryption.reportDisclosures, icon: ShieldCheck },
            ].map((item) => {
              const Icon = item.icon
              return (
                <div key={item.label} className="rounded-xl border border-[rgba(64,219,241,0.1)] bg-[rgba(0,0,0,0.18)] p-4">
                  <Icon className="h-4 w-4 text-[#40dbf1]" />
                  <p className="mt-3 text-2xl font-bold tabular-nums">{item.value}</p>
                  <p className="mt-1 text-xs text-[#9aabbb]">{item.label}</p>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      <LegacyPage title="Status page management" description="Create incidents, schedule maintenance, and configure monitored services.">
        <TrustSafetyHost tab="status" />
      </LegacyPage>
    </div>
  )
}
